import { useState } from 'react';
import { X, AlertTriangle, RefreshCw, Ban } from 'lucide-react';
import { Button } from './ui/Button';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

export function OrderCancelModal({ order, onClose, onSuccess }) {
  const [cancelType, setCancelType] = useState('with_refund'); // 'with_refund' or 'without_refund'
  const [reason, setReason] = useState('Customer request');
  const [isProcessing, setIsProcessing] = useState(false);

  const reasonOptions = [
    'Customer request',
    'Fraud',
    'Inventory',
    'Declined',
    'Other'
  ];

  const handleCancel = async () => {
    setIsProcessing(true);

    try {
      if (cancelType === 'with_refund') {
        // Cancel with full refund (recommended)
        const loadingToast = toast.loading('Erstelle Rückerstattung und storniere Bestellung...');

        const refundResult = await api.refundOrder(order.id, {
          reason: reason
        });

        if (refundResult.success) {
          toast.success('Rückerstattung erfolgreich erstellt. Buchungen werden automatisch storniert.', {
            id: loadingToast,
            duration: 5000
          });

          if (onSuccess) onSuccess();
          setTimeout(() => onClose(), 1500);
        } else {
          // Check if order is already fully refunded
          if (refundResult.error && refundResult.error.includes('already fully refunded')) {
            toast.dismiss(loadingToast);
            toast.error('Bestellung wurde bereits vollständig erstattet. Verwende "Ohne Rückerstattung stornieren".', {
              duration: 6000
            });
            // Automatically switch to without_refund mode
            setCancelType('without_refund');
          } else {
            toast.error(refundResult.error || 'Fehler beim Erstellen der Rückerstattung', { id: loadingToast });
          }
          setIsProcessing(false);
        }
      } else {
        // Cancel without refund (not recommended)
        const loadingToast = toast.loading('Storniere Bestellung ohne Rückerstattung...');

        const cancelResult = await api.cancelOrder(order.id, reason);

        if (cancelResult.success) {
          toast.success('Bestellung storniert. Buchungen werden automatisch freigegeben.', {
            id: loadingToast,
            duration: 5000
          });

          if (onSuccess) onSuccess();
          setTimeout(() => onClose(), 1500);
        } else {
          toast.error(cancelResult.error || 'Fehler beim Stornieren', { id: loadingToast });
          setIsProcessing(false);
        }
      }
    } catch (error) {
      console.error('Error canceling order:', error);
      toast.error('Fehler beim Verarbeiten der Anfrage');
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Bestellung stornieren</h2>
            <p className="text-sm text-gray-500 mt-1">
              Order: {order.order_id || `#${order.id}`}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Cancel Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Stornierungsart
            </label>

            <div className="space-y-3">
              {/* With Refund (Recommended) */}
              <button
                onClick={() => setCancelType('with_refund')}
                disabled={isProcessing}
                className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                  cancelType === 'with_refund'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <RefreshCw className={`w-5 h-5 mt-0.5 ${
                    cancelType === 'with_refund' ? 'text-green-600' : 'text-gray-400'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        Mit Rückerstattung
                      </span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                        Empfohlen
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Erstellt automatisch eine vollständige Rückerstattung in Shopify und storniert alle Buchungen.
                      Der Kunde erhält sein Geld zurück.
                    </p>
                  </div>
                </div>
              </button>

              {/* Without Refund (Warning) */}
              <button
                onClick={() => setCancelType('without_refund')}
                disabled={isProcessing}
                className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                  cancelType === 'without_refund'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Ban className={`w-5 h-5 mt-0.5 ${
                    cancelType === 'without_refund' ? 'text-red-600' : 'text-gray-400'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        Ohne Rückerstattung
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Storniert nur die Bestellung und gibt Buchungen frei, ohne Rückerstattung zu erstellen.
                    </p>
                    {cancelType === 'without_refund' && (
                      <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-red-800">
                            <strong className="font-semibold">Wichtig:</strong> Wenn Sie die Bestellung ohne Rückerstattung stornieren,
                            können Sie die Rückerstattung <strong>nicht mehr über das Admin Panel</strong> veranlassen.
                            Sie müssten dann manuell in Shopify eine Rückerstattung erstellen.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Reason Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Grund für Stornierung
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isProcessing}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {reasonOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Order Summary */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Bestelldetails</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Kunde:</span>
                <span className="font-medium text-gray-900">
                  {order.customer_name || `${order.first_name || ''} ${order.last_name || ''}`.trim() || 'Unbekannt'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Email:</span>
                <span className="font-medium text-gray-900">{order.customer_email || order.email || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span>Betrag:</span>
                <span className="font-medium text-gray-900">
                  {new Intl.NumberFormat('de-DE', {
                    style: 'currency',
                    currency: order.currency || 'EUR'
                  }).format(order.total_amount || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Status:</span>
                <span className="font-medium text-gray-900">{order.financial_status || order.status || '-'}</span>
              </div>
            </div>
          </div>

          {/* Warning for without_refund */}
          {cancelType === 'without_refund' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <strong className="font-semibold">Sind Sie sicher?</strong>
                  <p className="mt-1">
                    Ohne Rückerstattung wird der Kunde sein Geld nicht zurück erhalten.
                    Diese Option sollte nur in Ausnahmefällen verwendet werden (z.B. bei Betrug oder
                    wenn die Rückerstattung bereits manuell erfolgt ist).
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <Button
            onClick={onClose}
            variant="secondary"
            disabled={isProcessing}
          >
            Abbrechen
          </Button>

          <Button
            onClick={handleCancel}
            variant={cancelType === 'with_refund' ? 'success' : 'danger'}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Verarbeite...
              </>
            ) : (
              <>
                {cancelType === 'with_refund' ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Rückerstattung erstellen & Stornieren
                  </>
                ) : (
                  <>
                    <Ban className="w-4 h-4 mr-2" />
                    Nur stornieren (ohne Rückerstattung)
                  </>
                )}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
